import { useEffect} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notesApi } from '../services/api';
import { Note } from '../types/notes';
import { parseTags } from '../lib/tags';

export const NOTES_QUERY_KEY = ['notes'];
export const INBOX_NOTES_QUERY_KEY = ['notes', 'inbox'];

export const useNotes = () => {
  const queryClient = useQueryClient();
  const {data: notes, isLoading, isError, error} = useQuery({
    queryKey: NOTES_QUERY_KEY,
    queryFn: () => notesApi.getUserNotes(),
    staleTime: 20_000,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const deleteNotesMutation = useMutation({
    mutationFn: notesApi.deleteNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: notesApi.updateNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    }
  });

  const retryNoteMutation = useMutation({
    mutationFn: notesApi.retryNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    },
  });

  const deleteAnnotationMutation = useMutation({
    mutationFn: notesApi.deleteAnnotation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    },
  });

  const getNoteImageUrl = (note: Note) => {
    return notesApi.getNoteImageUrl(note.id);
  };

  const reload = () => {
    queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
  };

  const getTags = (notes: Note[]) => {
    return Array.from(
      new Set(
        notes.flatMap(note => parseTags(note.tags))
      )
    ).sort();
  };

  const deleteNotes = async (noteIds: number[]) => {
    return deleteNotesMutation.mutateAsync(noteIds);
  };

  useEffect(() => {
    // Exclude failed notes: OCR gives up after three attempts and `processed`
    // stays false forever, so polling on `!processed` alone kept refetching a
    // dead note every 5s indefinitely. This poll is also what wakes the backend
    // OCR worker, which has no timer of its own.
    const anyProcessing = notes?.some(note => !note.processed && !note.failed);

    let intervalId: NodeJS.Timeout | null = null;
    if (anyProcessing) {
      intervalId = setInterval(() => {
        reload();
      }, 5_000); // Refresh every 5 seconds 
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [notes]);

  return {
    // Data
    notes: notes ?? [],
    isLoading,
    isError,
    error,
    
    // Actions
    getNote: notesApi.getNote,
    getNoteImageUrl,
    updateNote: updateNoteMutation.mutateAsync,
    deleteNotes,
    deleteAnnotation: deleteAnnotationMutation.mutateAsync,
    retryNote: retryNoteMutation.mutateAsync,
    isRetrying: retryNoteMutation.isPending,
    reload,
    getTags,
    
    // Mutation state
    isUpdating: updateNoteMutation.isPending,
    updateError: updateNoteMutation.error,
    isDeleting: deleteNotesMutation.isPending,
    deleteError: deleteNotesMutation.error,
  };
};

// Utility hook for filtering notes
export const useFilteredNotes = (filterTag?: string) => {
  const { notes, ...rest } = useNotes();
  
  const filteredNotes = filterTag 
    ? notes.filter(note => parseTags(note.tags).includes(filterTag))
    : notes;

  return {
    notes: filteredNotes,
    allNotes: notes,
    ...rest
  };
};

// Hook specifically for inbox notes
export const useInboxNotes = () => {
  const queryClient = useQueryClient();
  const {data: notes, isLoading, isError, error} = useQuery({
    queryKey: INBOX_NOTES_QUERY_KEY,
    queryFn: () => notesApi.getUserNotes(true), // Only inboxed notes
    staleTime: 20_000,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const deleteNotesMutation = useMutation({
    mutationFn: notesApi.deleteNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_NOTES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: notesApi.updateNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_NOTES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    }
  });

  const getNoteImageUrl = (note: Note) => {
    return notesApi.getNoteImageUrl(note.id);
  };

  const reload = () => {
    queryClient.invalidateQueries({ queryKey: INBOX_NOTES_QUERY_KEY });
  };

  const getTags = (notes: Note[]) => {
    return Array.from(
      new Set(
        notes.flatMap(note => parseTags(note.tags))
      )
    ).sort();
  };

  const deleteNotes = async (noteIds: number[]) => {
    return deleteNotesMutation.mutateAsync(noteIds);
  };

  useEffect(() => {
    // Exclude failed notes: OCR gives up after three attempts and `processed`
    // stays false forever, so polling on `!processed` alone kept refetching a
    // dead note every 5s indefinitely. This poll is also what wakes the backend
    // OCR worker, which has no timer of its own.
    const anyProcessing = notes?.some(note => !note.processed && !note.failed);

    let intervalId: NodeJS.Timeout | null = null;
    if (anyProcessing) {
      intervalId = setInterval(() => {
        reload();
      }, 5_000); // Refresh every 5 seconds 
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [notes]);

  return {
    // Data
    notes: notes ?? [],
    isLoading,
    isError,
    error,
    
    // Actions
    getNote: notesApi.getNote,
    getNoteImageUrl,
    updateNote: updateNoteMutation.mutateAsync,
    deleteNotes,
    reload,
    getTags,
    
    // Mutation state
    isUpdating: updateNoteMutation.isPending,
    updateError: updateNoteMutation.error,
    isDeleting: deleteNotesMutation.isPending,
    deleteError: deleteNotesMutation.error,
  };
};
